import * as THREE from "three";
import { makeToon, toonMat } from "../toon.ts";
import { UNIT_BOX } from "../meshkit.ts";
import { biomeColorFor, hash } from "../terrain.ts";

// ---------------------------------------------------------------------------
// Edge-preview strip builders (seamless map edges). Low-res analogs of the
// terrain.ts chunk pipeline, parameterized by an explicit heightFn/waterLevel
// instead of the active map's module state — so they can paint a NEIGHBOR
// map's terrain (via its makeTerrainCtx) or the active map's noise continued
// past the bounds (the "endless filler" — heightAt has no bounds clamp).
//
// CLIENT-ONLY presentation: never imported by world.ts or the server, no
// colliders (client-only geometry with colliders would desync the server's
// movement validation), no entities. Fog (60→135) swallows the strips' far
// ends, so ~150u of depth is all that can ever be seen.
//
// v1 limitations (deliberate): no roads, no tree scatter in previews — the
// neighbor reads as terrain + water + landmark massing under fog.
// ---------------------------------------------------------------------------

/** ONE shared material for every strip tile (the terrainMat twin — lazy,
 *  NOT `owned`: it survives edge deactivations and map swaps). */
let stripMat: THREE.MeshToonMaterial | null = null;

export type StripTileOpts = {
  /** World-space ground height (already blended/translated by the caller). */
  heightFn: (x: number, z: number) => number;
  /** Waterline the biome colors judge "beach"/sand against. */
  waterLevel: number;
  /** Optional per-vertex waterline — used by linked-edge tiles to blend the
   *  active map's waterline into the neighbor's over the height-blend band, so
   *  near-seam ground (still active-dominated) isn't tinted against the wrong
   *  map's sea level. Overrides `waterLevel` where present. */
  waterLevelFn?: (x: number, z: number) => number;
  /** Rect min corner + extent, world coords. */
  x0: number;
  z0: number;
  w: number;
  d: number;
  /** Target world-units per vertex (5 = half the active map's 2.5 density). */
  res: number;
};

/** Build ONE low-res strip tile — the buildTerrainChunk pipeline (displace →
 *  non-indexed faceted normals → per-vertex biome color) over an arbitrary
 *  rect + heightFn. Adjacent tiles sharing a border edge stay watertight as
 *  long as their spans (and so their vertex grids) line up. */
export function buildStripTile(o: StripTileOpts): THREE.Mesh {
  const res = o.res;
  const segX = Math.max(1, Math.round(o.w / res));
  const segZ = Math.max(1, Math.round(o.d / res));
  stripMat ??= makeToon({ vertexColors: true });

  const plane = new THREE.PlaneGeometry(o.w, o.d, segX, segZ);
  plane.rotateX(-Math.PI / 2);
  plane.translate(o.x0 + o.w / 2, 0, o.z0 + o.d / 2); // bake world position into the verts

  const ppos = plane.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < ppos.count; i++) {
    ppos.setY(i, o.heightFn(ppos.getX(i), ppos.getZ(i)));
  }

  const geo = plane.toNonIndexed(); // faceted normals, same as the real terrain
  geo.computeVertexNormals();
  plane.dispose();

  const pos = geo.attributes.position as THREE.BufferAttribute;
  const normal = geo.attributes.normal as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const px = pos.getX(i), pz = pos.getZ(i);
    const c = biomeColorFor(
      pos.getY(i),
      normal.getY(i),
      hash(px * 3.1, pz * 1.7),
      o.waterLevelFn ? o.waterLevelFn(px, pz) : o.waterLevel,
    );
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(geo, stripMat);
  mesh.castShadow = false;
  mesh.receiveShadow = false; // beyond the sun shadow camera anyway
  return mesh;
}

/** Static water sheet for a strip rect (the buildWater recipe minus the
 *  ripple — fog + distance hide a still surface). Material is per-build and
 *  tagged `owned` so disposeGroup reclaims it on edge deactivation. */
export function stripWater(
  x0: number,
  z0: number,
  w: number,
  d: number,
  waterLevel: number,
): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(w, d, 1, 1).rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(
    geo,
    makeToon({ color: 0x3a78b5, transparent: true, opacity: 0.82, depthWrite: false }, { rim: 0.5 }),
  );
  (mesh.material as THREE.Material).userData.owned = true;
  // A hair under the real plane's rest level: the active water only ever
  // ripples UPWARD, so where the two sheets meet at the seam the live one
  // draws over this one instead of z-fighting.
  mesh.position.set(x0 + w / 2, waterLevel - 0.02, z0 + d / 2);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

const LM_WALL = 0xcfc4a8; // plaster
const LM_STONE = 0x8d8878;
const LM_ROOF = 0x8a4a32;

/** Cheap massing stand-in for one neighbor structure placement: a toon box
 *  sized to the real footprint (width × depth, world units) + a pyramid cap,
 *  rotated to the placement's real facing. Reads as "a building over there"
 *  through the fog; never the real structure build (which would clobber the
 *  ACTIVE map's flatten state via its pad). Boxes scale the shared UNIT_BOX —
 *  no per-tile geometry churn as edges build/dispose on the hysteresis band. */
export function landmarkBlock(
  x: number,
  z: number,
  groundY: number,
  w: number,
  d: number,
  rot: number,
): THREE.Group {
  const g = new THREE.Group();
  const seed = hash(x * 1.3, z * 2.7);
  const bw = Math.max(4, w), bd = Math.max(4, d);
  const h = Math.min(9, Math.max(3.5, Math.max(bw, bd) * 0.7));

  const base = new THREE.Mesh(UNIT_BOX, toonMat(seed < 0.5 ? LM_WALL : LM_STONE));
  base.scale.set(bw, h, bd);
  base.position.y = h / 2;
  g.add(base);

  const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.max(bw, bd) * 0.62, h * 0.55, 4), toonMat(LM_ROOF));
  roof.position.y = h + h * 0.275;
  roof.rotation.y = Math.PI / 4;
  g.add(roof);

  g.position.set(x, groundY - 0.3, z); // slight sink — the ground is uncarved
  g.rotation.y = rot; // the placement's real facing (turns × 90°)
  g.traverse((o) => { o.castShadow = false; o.receiveShadow = false; });
  return g;
}

const CAIRN_STONE = 0x9aa3ad; // cool weathered granite, echoing the stone arch

/** A boundary cairn — 3 stacked, slightly skewed stones marking a linked seam.
 *  Pure presentation: NO collider (it stands beyond the movement clamp). */
export function boundaryCairn(seed: number): THREE.Group {
  const g = new THREE.Group();
  const s = 0.9 + hash(seed, 1.7) * 0.5; // per-cairn scale jitter
  let y = 0;
  for (let i = 0; i < 3; i++) {
    const w = (1.5 - i * 0.38) * s;
    const h = (0.75 - i * 0.12) * s;
    const stone = new THREE.Mesh(UNIT_BOX, toonMat(CAIRN_STONE)); // shared geom, scaled
    stone.scale.set(w, h, w * 0.85);
    stone.position.set(
      (hash(seed + i, 3.1) - 0.5) * 0.3,
      y + h / 2,
      (hash(seed + i, 7.9) - 0.5) * 0.3,
    );
    stone.rotation.y = (hash(seed + i, 5.3) - 0.5) * 0.9;
    stone.castShadow = false;
    stone.receiveShadow = false;
    g.add(stone);
    y += h * 0.92;
  }
  g.rotation.y = hash(seed, 11.3) * Math.PI;
  return g;
}
